import { sha256FloorplanAsset } from "./floorplanImportUtils";

export const DEFAULT_FLOORPLAN_PREVIEW_MAX_DIMENSION = 2200;

const PNG_MIME_TYPE = "image/png";
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const SHA256_PATTERN = /^[a-f0-9]{64}$/;

function asUint8Array(value, owner) {
	if (value instanceof Uint8Array) {
		return value;
	}
	if (value instanceof ArrayBuffer) {
		return new Uint8Array(value);
	}
	if (ArrayBuffer.isView(value)) {
		return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
	}
	throw new Error(`${owner} does not contain Uint8Array-compatible bytes.`);
}

function positiveInteger(value) {
	return Number.isInteger(value) && value > 0 ? value : null;
}

function readUint32BigEndian(bytes, offset) {
	return (
		(bytes[offset] * 0x1000000)
		+ (bytes[offset + 1] << 16)
		+ (bytes[offset + 2] << 8)
		+ bytes[offset + 3]
	) >>> 0;
}

export function readFloorplanPngDimensions(value) {
	let bytes;
	try {
		bytes = asUint8Array(value, "PNG asset");
	} catch (_error) {
		return null;
	}
	if (bytes.byteLength < 24) {
		return null;
	}
	for (let index = 0; index < PNG_SIGNATURE.length; index += 1) {
		if (bytes[index] !== PNG_SIGNATURE[index]) {
			return null;
		}
	}
	if (
		bytes[12] !== 0x49
		|| bytes[13] !== 0x48
		|| bytes[14] !== 0x44
		|| bytes[15] !== 0x52
	) {
		return null;
	}
	const width = readUint32BigEndian(bytes, 16);
	const height = readUint32BigEndian(bytes, 20);
	return width > 0 && height > 0 ? { width, height } : null;
}

export function calculateFloorplanPreviewSize(width, height, maxDimension = DEFAULT_FLOORPLAN_PREVIEW_MAX_DIMENSION) {
	const sourceWidth = positiveInteger(width);
	const sourceHeight = positiveInteger(height);
	const limit = positiveInteger(maxDimension);
	if (!sourceWidth || !sourceHeight) {
		throw new Error("Floorplan preview source dimensions must be positive integers.");
	}
	if (!limit) {
		throw new Error("Floorplan preview maximum dimension must be a positive integer.");
	}
	const scale = Math.min(1, limit / Math.max(sourceWidth, sourceHeight));
	return {
		width: Math.max(1, Math.round(sourceWidth * scale)),
		height: Math.max(1, Math.round(sourceHeight * scale)),
		scale
	};
}

function dimensionsFromAsset(asset, bytes) {
	const pngDimensions = readFloorplanPngDimensions(bytes);
	if (pngDimensions) {
		return pngDimensions;
	}
	const width = positiveInteger(asset && asset.width);
	const height = positiveInteger(asset && asset.height);
	return width && height ? { width, height } : null;
}

function getBrowserGlobal(name) {
	return typeof window !== "undefined" ? window[name] : undefined;
}

async function blobToBytes(blob) {
	if (blob && typeof blob.arrayBuffer === "function") {
		return new Uint8Array(await blob.arrayBuffer());
	}
	const FileReaderConstructor = getBrowserGlobal("FileReader");
	if (!FileReaderConstructor) {
		throw new Error("This browser cannot read a generated floorplan preview Blob.");
	}
	return new Promise((resolve, reject) => {
		const reader = new FileReaderConstructor();
		reader.onerror = () => reject(reader.error || new Error("Unable to read the generated floorplan preview."));
		reader.onload = () => resolve(new Uint8Array(reader.result));
		reader.readAsArrayBuffer(blob);
	});
}

function loadHtmlImage(blob) {
	const ImageConstructor = getBrowserGlobal("Image");
	const URLApi = getBrowserGlobal("URL");
	if (!ImageConstructor || !URLApi || typeof URLApi.createObjectURL !== "function") {
		return Promise.reject(new Error("This browser cannot decode floorplan images."));
	}
	return new Promise((resolve, reject) => {
		const url = URLApi.createObjectURL(blob);
		const image = new ImageConstructor();
		image.onload = () => resolve({
			drawable: image,
			width: image.naturalWidth || image.width,
			height: image.naturalHeight || image.height,
			cleanup: () => URLApi.revokeObjectURL(url)
		});
		image.onerror = () => {
			URLApi.revokeObjectURL(url);
			reject(new Error("The browser could not decode a floorplan image."));
		};
		image.src = url;
	});
}

async function decodeBrowserImage(blob, resize = null) {
	const createImageBitmapFunction = getBrowserGlobal("createImageBitmap");
	if (typeof createImageBitmapFunction === "function") {
		try {
			const bitmap = resize
				? await createImageBitmapFunction(blob, {
					resizeWidth: resize.width,
					resizeHeight: resize.height,
					resizeQuality: "high"
				})
				: await createImageBitmapFunction(blob);
			return {
				drawable: bitmap,
				width: bitmap.width,
				height: bitmap.height,
				cleanup: () => {
					if (typeof bitmap.close === "function") {
						bitmap.close();
					}
				}
			};
		} catch (_error) {
			// Some browsers expose createImageBitmap but cannot decode every image
			// format through it. The HTMLImageElement fallback covers those cases.
		}
	}
	return loadHtmlImage(blob);
}

function createBrowserCanvas(width, height) {
	const OffscreenCanvasConstructor = getBrowserGlobal("OffscreenCanvas");
	if (typeof OffscreenCanvasConstructor === "function") {
		return new OffscreenCanvasConstructor(width, height);
	}
	const browserDocument = getBrowserGlobal("document");
	if (!browserDocument || typeof browserDocument.createElement !== "function") {
		throw new Error("This browser cannot create a floorplan preview canvas.");
	}
	const canvas = browserDocument.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	return canvas;
}

function canvasToPngBlob(canvas) {
	if (typeof canvas.convertToBlob === "function") {
		return canvas.convertToBlob({ type: PNG_MIME_TYPE });
	}
	if (typeof canvas.toBlob !== "function") {
		return Promise.reject(new Error("This browser cannot encode a floorplan preview as PNG."));
	}
	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (blob) {
				resolve(blob);
			} else {
				reject(new Error("The browser returned an empty floorplan preview."));
			}
		}, PNG_MIME_TYPE);
	});
}

/**
 * Default browser implementation. It is intentionally the only code path in
 * this module that touches Blob, Image, createImageBitmap, or canvas APIs.
 */
export async function rasterizeFloorplanPreviewInBrowser(asset, options = {}) {
	const maxDimension = positiveInteger(options.maxDimension) || DEFAULT_FLOORPLAN_PREVIEW_MAX_DIMENSION;
	const bytes = asUint8Array(asset && asset.bytes, `Floorplan asset ${asset && asset.id ? asset.id : "(unknown)"}`);
	const BlobConstructor = getBrowserGlobal("Blob");
	if (!BlobConstructor) {
		throw new Error("This browser cannot create a floorplan image Blob.");
	}
	const blob = new BlobConstructor([bytes], { type: asset.mimeType || "application/octet-stream" });
	const knownSourceWidth = positiveInteger(options.sourceWidth);
	const knownSourceHeight = positiveInteger(options.sourceHeight);
	const resize = knownSourceWidth && knownSourceHeight && Math.max(knownSourceWidth, knownSourceHeight) > maxDimension
		? calculateFloorplanPreviewSize(knownSourceWidth, knownSourceHeight, maxDimension)
		: null;
	const decoded = await decodeBrowserImage(blob, resize);
	try {
		const sourceWidth = knownSourceWidth || positiveInteger(decoded.width);
		const sourceHeight = knownSourceHeight || positiveInteger(decoded.height);
		if (!sourceWidth || !sourceHeight) {
			throw new Error(`Floorplan asset ${asset.id} decoded without valid dimensions.`);
		}
		if (Math.max(sourceWidth, sourceHeight) <= maxDimension) {
			return null;
		}
		const target = calculateFloorplanPreviewSize(sourceWidth, sourceHeight, maxDimension);
		const canvas = createBrowserCanvas(target.width, target.height);
		const context = canvas.getContext("2d", { alpha: true });
		if (!context) {
			throw new Error("This browser could not create a 2D floorplan preview context.");
		}
		context.clearRect(0, 0, target.width, target.height);
		context.imageSmoothingEnabled = true;
		if ("imageSmoothingQuality" in context) {
			context.imageSmoothingQuality = "high";
		}
		context.drawImage(decoded.drawable, 0, 0, target.width, target.height);
		const previewBlob = await canvasToPngBlob(canvas);
		return {
			bytes: await blobToBytes(previewBlob),
			mimeType: PNG_MIME_TYPE,
			width: target.width,
			height: target.height,
			sourceWidth,
			sourceHeight
		};
	} finally {
		decoded.cleanup();
	}
}

function validateSourceAsset(asset, seenAssetIds) {
	if (!asset || typeof asset !== "object") {
		throw new Error("Floorplan preview source assets must be descriptor objects.");
	}
	if (typeof asset.id !== "string" || !asset.id) {
		throw new Error("Every floorplan preview source asset must have a non-empty id.");
	}
	if (seenAssetIds.has(asset.id)) {
		throw new Error(`Floorplan preview source asset id ${asset.id} is duplicated.`);
	}
	seenAssetIds.add(asset.id);
	if (typeof asset.mimeType !== "string" || !asset.mimeType.startsWith("image/")) {
		throw new Error(`Floorplan asset ${asset.id} must have an image MIME type.`);
	}
	return asUint8Array(asset.bytes, `Floorplan asset ${asset.id}`);
}

function normalizeRasterizedResult(result, asset, knownSourceDimensions, maxDimension) {
	if (result === null || result === undefined) {
		if (knownSourceDimensions && Math.max(knownSourceDimensions.width, knownSourceDimensions.height) > maxDimension) {
			throw new Error(`Floorplan preview rasterizer skipped oversized asset ${asset.id}.`);
		}
		return null;
	}
	if (!result || typeof result !== "object") {
		throw new Error(`Floorplan preview rasterizer returned an invalid result for ${asset.id}.`);
	}
	const sourceWidth = positiveInteger(result.sourceWidth)
		|| (knownSourceDimensions && knownSourceDimensions.width);
	const sourceHeight = positiveInteger(result.sourceHeight)
		|| (knownSourceDimensions && knownSourceDimensions.height);
	if (!sourceWidth || !sourceHeight) {
		throw new Error(`Floorplan preview rasterizer did not report source dimensions for ${asset.id}.`);
	}
	if (Math.max(sourceWidth, sourceHeight) <= maxDimension) {
		return null;
	}
	const expected = calculateFloorplanPreviewSize(sourceWidth, sourceHeight, maxDimension);
	const width = positiveInteger(result.width);
	const height = positiveInteger(result.height);
	if (!width || !height || width !== expected.width || height !== expected.height) {
		throw new Error(`Floorplan preview rasterizer returned incorrect dimensions for ${asset.id}; expected ${expected.width}x${expected.height}.`);
	}
	if (result.mimeType && result.mimeType !== PNG_MIME_TYPE) {
		throw new Error(`Floorplan preview rasterizer must return PNG bytes for ${asset.id}.`);
	}
	const bytes = asUint8Array(result.bytes, `Floorplan preview for ${asset.id}`);
	const pngDimensions = readFloorplanPngDimensions(bytes);
	if (!pngDimensions || pngDimensions.width !== width || pngDimensions.height !== height) {
		throw new Error(`Floorplan preview rasterizer returned invalid PNG bytes for ${asset.id}.`);
	}
	return { bytes, width, height, sourceWidth, sourceHeight };
}

function equalBytes(left, right) {
	if (left.byteLength !== right.byteLength) {
		return false;
	}
	for (let index = 0; index < left.byteLength; index += 1) {
		if (left[index] !== right[index]) {
			return false;
		}
	}
	return true;
}

function normalizeSha256(value, assetId) {
	const sha256 = typeof value === "string" ? value.toLowerCase() : "";
	if (!SHA256_PATTERN.test(sha256)) {
		throw new Error(`Floorplan preview hasher returned an invalid SHA-256 value for ${assetId}.`);
	}
	return sha256;
}

/**
 * Prepares separate screen previews without changing or replacing canonical
 * imported assets. Processing is sequential to bound decoded-image memory.
 */
export async function prepareFloorplanPreviewRenditions(sourceAssets, options = {}) {
	if (!Array.isArray(sourceAssets)) {
		throw new Error("Floorplan preview source assets must be an array.");
	}
	const maxDimension = options.maxDimension === undefined
		? DEFAULT_FLOORPLAN_PREVIEW_MAX_DIMENSION
		: positiveInteger(options.maxDimension);
	if (!maxDimension) {
		throw new Error("Floorplan preview maximum dimension must be a positive integer.");
	}
	const rasterize = options.rasterize || rasterizeFloorplanPreviewInBrowser;
	const hashBytes = options.hashBytes || sha256FloorplanAsset;
	const onProgress = typeof options.onProgress === "function" ? options.onProgress : () => {};
	if (typeof rasterize !== "function") {
		throw new Error("Floorplan preview rasterizer must be a function.");
	}
	if (typeof hashBytes !== "function") {
		throw new Error("Floorplan preview hasher must be a function.");
	}

	const renditionsByAssetId = {};
	const generatedAssetsById = new Map();
	const seenAssetIds = new Set();
	let canonicalBytes = 0;
	let generatedSourceAssetCount = 0;
	let completed = 0;
	onProgress({ completed, total: sourceAssets.length, label: "Preparing floorplan previews…" });

	for (const asset of sourceAssets) {
		const canonicalAssetBytes = validateSourceAsset(asset, seenAssetIds);
		canonicalBytes += canonicalAssetBytes.byteLength;
		const sourceDimensions = dimensionsFromAsset(asset, canonicalAssetBytes);
		if (sourceDimensions && Math.max(sourceDimensions.width, sourceDimensions.height) <= maxDimension) {
			completed += 1;
			onProgress({ completed, total: sourceAssets.length, label: `Prepared ${completed} of ${sourceAssets.length} floorplan previews` });
			continue;
		}
		const target = sourceDimensions
			? calculateFloorplanPreviewSize(sourceDimensions.width, sourceDimensions.height, maxDimension)
			: null;
		const rasterized = normalizeRasterizedResult(await rasterize(asset, {
			maxDimension,
			sourceWidth: sourceDimensions && sourceDimensions.width,
			sourceHeight: sourceDimensions && sourceDimensions.height,
			targetWidth: target && target.width,
			targetHeight: target && target.height
		}), asset, sourceDimensions, maxDimension);
		if (!rasterized) {
			completed += 1;
			onProgress({ completed, total: sourceAssets.length, label: `Prepared ${completed} of ${sourceAssets.length} floorplan previews` });
			continue;
		}

		const sha256 = normalizeSha256(await hashBytes(rasterized.bytes, {
			sourceAssetId: asset.id,
			width: rasterized.width,
			height: rasterized.height,
			mimeType: PNG_MIME_TYPE
		}), asset.id);
		const id = `rendition-${sha256}`;
		let descriptor = generatedAssetsById.get(id);
		if (descriptor) {
			if (
				descriptor.width !== rasterized.width
				|| descriptor.height !== rasterized.height
				|| !equalBytes(descriptor.bytes, rasterized.bytes)
			) {
				throw new Error(`SHA-256 collision while preparing floorplan preview for ${asset.id}.`);
			}
		} else {
			descriptor = {
				id,
				sha256,
				mimeType: PNG_MIME_TYPE,
				byteLength: rasterized.bytes.byteLength,
				fileName: `${sha256}.png`,
				width: rasterized.width,
				height: rasterized.height,
				bytes: rasterized.bytes,
				sourceAssetIds: []
			};
			generatedAssetsById.set(id, descriptor);
		}
		descriptor.sourceAssetIds.push(asset.id);
		renditionsByAssetId[asset.id] = descriptor;
		generatedSourceAssetCount += 1;
		completed += 1;
		onProgress({ completed, total: sourceAssets.length, label: `Prepared ${completed} of ${sourceAssets.length} floorplan previews` });
	}

	const assets = Array.from(generatedAssetsById.values());
	const previewBytes = assets.reduce((total, asset) => total + asset.byteLength, 0);
	return {
		renditionsByAssetId,
		assets,
		inventory: {
			maxDimension,
			sourceAssetCount: sourceAssets.length,
			generatedSourceAssetCount,
			skippedSourceAssetCount: sourceAssets.length - generatedSourceAssetCount,
			uniquePreviewAssetCount: assets.length,
			deduplicatedPreviewCount: generatedSourceAssetCount - assets.length,
			canonicalBytes,
			previewBytes
		}
	};
}

export default prepareFloorplanPreviewRenditions;
